import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  BaseAPI,
  CompanySpaceMandatoryParams,
  CompanySpaceUpdate,
  CompanySpaceUpdateResponse,
  CompanySpaceUpdateResponseFromJSON,
  JSONApiResponse,
} from '../generated-api';
import { API_CONFIG } from '../app/api-config';

export type CompanySettings = CompanySpaceUpdateResponse;

/**
 * The generated CompanySpaceUpdate is serialized field by field, and the backend reads
 * the mandatory flags from `mandatory_params` and the logo from `file_id` -- neither of
 * which the spec knows, so those would be silently dropped.
 */
export type CompanySettingsPatch = Omit<CompanySpaceUpdate, 'mandatory_params_hash'> & {
  mandatory_params?: CompanySpaceMandatoryParams;
  file_id?: string;
};

// the spec describes only PUT /company_spaces; GET of the same resource is not generated
class CompanySettingsApi extends BaseAPI {
  async get(): Promise<CompanySettings> {
    const response = await this.request({
      path: '/company_spaces',
      method: 'GET',
      headers: {},
      query: {},
    });
    return new JSONApiResponse(response, CompanySpaceUpdateResponseFromJSON).value();
  }

  async update(body: CompanySettingsPatch): Promise<CompanySettings> {
    const response = await this.request({
      path: '/company_spaces',
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      query: {},
      body,
    });
    return new JSONApiResponse(response, CompanySpaceUpdateResponseFromJSON).value();
  }
}

const companySettingsApi = new CompanySettingsApi(API_CONFIG);

export const fetchCompanySettings = createAsyncThunk('companySettings/fetch', async () => {
  return await companySettingsApi.get();
});

export const updateCompanySettings = createAsyncThunk(
  'companySettings/update',
  async (patch: CompanySettingsPatch) => {
    return await companySettingsApi.update(patch);
  }
);
